/**
 * Google Analytics Service
 *
 * Wrapper around react-ga4 for Google Analytics 4 tracking.
 * Works alongside Application Insights for comprehensive analytics.
 *
 * @module services/google-analytics
 */

import ReactGA from 'react-ga4';
import { logger } from '../utils/logger';

/**
 * Google Analytics Service
 *
 * Singleton service for tracking with Google Analytics 4.
 */
class GoogleAnalyticsService {
  private isInitialized = false;

  /**
   * Initialize Google Analytics
   *
   * @param measurementId - GA4 Measurement ID (G-XXXXXXXXXX)
   */
  initialize(measurementId: string): void {
    if (this.isInitialized || !measurementId) {
      return;
    }

    try {
      ReactGA.initialize(measurementId, {
        gaOptions: {
          // Anonymize IP for privacy
          anonymize_ip: true,
        },
      });

      this.isInitialized = true;

      logger.info('Google Analytics initialized', { measurementId });
    } catch (error) {
      logger.error('Failed to initialize Google Analytics', error);
    }
  }

  /**
   * Check if GA is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Track page view
   *
   * @param path - Page path
   * @param title - Page title
   */
  trackPageView(path: string, title?: string): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      ReactGA.send({
        hitType: 'pageview',
        page: path,
        title: title || document.title,
      });
    } catch (error) {
      logger.error('Failed to track GA page view', error, { path, title });
    }
  }

  /**
   * Track custom event
   *
   * @param category - Event category
   * @param action - Event action
   * @param label - Event label (optional)
   * @param value - Event value (optional)
   */
  trackEvent(category: string, action: string, label?: string, value?: number): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      ReactGA.event({
        category,
        action,
        label,
        value,
      });
    } catch (error) {
      logger.error('Failed to track GA event', error, { category, action });
    }
  }

  /**
   * Track custom event with GA4 recommended event names
   *
   * @param eventName - GA4 event name (e.g., 'login', 'sign_up', 'purchase')
   * @param params - Event parameters
   */
  trackGA4Event(eventName: string, params?: Record<string, any>): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      ReactGA.event(eventName, params);
    } catch (error) {
      logger.error('Failed to track GA4 event', error, { eventName });
    }
  }

  /**
   * Set user ID for cross-device tracking
   *
   * @param userId - User ID
   */
  setUserId(userId: string): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      ReactGA.set({ userId });
      logger.debug('Google Analytics: User ID set');
    } catch (error) {
      logger.error('Failed to set GA user ID', error);
    }
  }

  /**
   * Set user properties
   *
   * @param properties - User properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      ReactGA.set(properties);
    } catch (error) {
      logger.error('Failed to set GA user properties', error);
    }
  }

  /**
   * Track timing (performance)
   *
   * @param category - Timing category
   * @param variable - Timing variable
   * @param value - Time in milliseconds
   * @param label - Timing label (optional)
   */
  trackTiming(category: string, variable: string, value: number, label?: string): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      ReactGA.event('timing_complete', {
        name: variable,
        value,
        event_category: category,
        event_label: label,
      });
    } catch (error) {
      logger.error('Failed to track GA timing', error, { category, variable, value });
    }
  }

  /**
   * Track exception
   *
   * @param description - Error description
   * @param fatal - Whether the error was fatal
   */
  trackException(description: string, fatal: boolean = false): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      ReactGA.event('exception', {
        description,
        fatal,
      });
    } catch (error) {
      logger.error('Failed to track GA exception', error, { description, fatal });
    }
  }
}

// Export singleton instance
export const googleAnalyticsService = new GoogleAnalyticsService();
